import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Direction, HistoryKpiCardData } from '../models/history-kpi-card-data';
import { UIChart } from 'primeng/chart';
import { KpiChartOptions } from '../models/kpi-chart-options';
import { LocalizedDecimalPipe } from '@myarvato/shared/localized-decimal-pipe';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { UserTrackingService } from '@myarvato/core/services';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'transport-history-kpi-card',
  imports: [UIChart, LocalizedDecimalPipe, Tooltip, TranslatePipe, ButtonModule],
  templateUrl: './history-kpi-card.component.html',
  styleUrl: './history-kpi-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryKpiCardComponent {
  // Injections
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly router = inject(Router);

  // Inputs
  public readonly kpiData = input.required<HistoryKpiCardData>();

  // Fields
  protected readonly chartOptions = KpiChartOptions;
  private readonly trackingCategory = 'Transport - Overview - Key Indicators - Kpi Card';

  // Change detection
  protected readonly chartData = computed(() => {
    const kpiData = this.kpiData();

    return {
      labels: Object.keys(kpiData.data),
      datasets: [
        {
          data: Object.values(kpiData.data),
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: kpiData.chartLineColor,
          backgroundColor: kpiData.chartFillColor,
          fill: true,
          borderWidth: 1,
        },
      ],
    };
  });

  protected readonly valueComparisonPercentage = computed(() => {
    const kpiData = this.kpiData();

    if (kpiData.valueComparison === undefined || kpiData.valueComparison === 0 || kpiData.value === 0) {
      return undefined;
    }

    return (kpiData.value / kpiData.valueComparison) * 100;
  });

  protected readonly valueChangePercentage = computed(() => {
    const kpiData = this.kpiData();

    if (!kpiData.previousValue) {
      return undefined;
    }

    return ((kpiData.value - kpiData.previousValue) / kpiData.previousValue) * 100;
  });

  protected readonly isPositiveDirection = computed(() => {
    const change = this.valueChangePercentage();
    if (!change) {
      return false;
    }

    const semantic = this.kpiData().kpiSemanticDirection;

    if (semantic === Direction.INCREASE_IS_GOOD) {
      return change > 0;
    }

    if (semantic === Direction.DECREASE_IS_GOOD) {
      return change < 0;
    }

    return false;
  });

  protected readonly isNegativeDirection = computed(() => {
    const change = this.valueChangePercentage();
    if (!change) {
      return false;
    }

    const semantic = this.kpiData().kpiSemanticDirection;

    if (semantic === Direction.INCREASE_IS_GOOD) {
      return change < 0;
    }

    if (semantic === Direction.DECREASE_IS_GOOD) {
      return change > 0;
    }

    return false;
  });

  protected readonly isValueChangeIncrease = computed(() => {
    const change = this.valueChangePercentage();
    if (change === undefined) {
      return false;
    }
    return change > 0;
  });

  protected onGoToDetailsClick() {
    this.userTrackingService.trackEvent(this.trackingCategory, 'Goto transport list', this.kpiData().title);

    this.router
      .navigate(['transport', 'list'], {
        state: {
          'disable-selected-view': true,
          'list-filter': this.kpiData().listFilter,
        },
      })
      .then();
  }
}
